"use client"

import * as React from "react"
import type { Editor, Range } from "@tiptap/react"
import type { EmojiItem } from "@tiptap/extension-emoji"
import { ScrollArea } from "@deepguide-ai/ui/scroll-area"

// --- Tiptap UI ---
import type {
  SuggestionItem,
  SuggestionMenuProps,
  SuggestionMenuRenderProps,
} from "@/tiptap/tiptap-ui-utils/suggestion-menu"
import { SuggestionMenu } from "@/tiptap/tiptap-ui-utils/suggestion-menu"
import {
  EmojiMenuItem,
  getFilteredEmojis,
} from "@/tiptap/tiptap-ui/emoji-menu"
import { Card, CardBody } from "@/tiptap/tiptap-ui-primitive/card"
import { ButtonGroup } from "@/tiptap/tiptap-ui-primitive/button"

export type EmojiDropdownMenuProps = Omit<
  SuggestionMenuProps,
  "items" | "children"
>

export const EmojiDropdownMenu = (props: EmojiDropdownMenuProps) => {
  return (
    <SuggestionMenu
      char=":"
      pluginKey="emojiDropdownMenu"
      decorationClass="tiptap-emoji-decoration"
      selector="tiptap-emoji-dropdown-menu"
      items={getSuggestionItems}
      {...props}
    >
      {(props) => <EmojiList {...props} />}
    </SuggestionMenu>
  )
}

const getSuggestionItems = async (props: { query: string; editor: Editor }) => {
  const emojis: EmojiItem[] = props.editor.extensionStorage.emoji.emojis || []
  const filteredEmojis = getFilteredEmojis({ query: props.query, emojis })

  return filteredEmojis.map(
    (emoji): SuggestionItem => ({
      title: emoji.name,
      subtext: emoji.shortcodes.join(", "),
      context: emoji,
      onSelect: (props: {
        editor: Editor
        range: Range
        context?: EmojiItem
      }) => {
        if (!props.editor || !props.range || !props.context) return
        props.editor.chain().focus().setEmoji(props.context.name).run()
      },
    })
  )
}

const EmojiList = ({
  items,
  selectedIndex,
  onSelect,
}: SuggestionMenuRenderProps<EmojiItem>) => {
  const renderedItems = React.useMemo(() => {
    const rendered: React.ReactElement[] = []

    items.forEach((item, index) => {
      if (!item.context) return

      rendered.push(
        <EmojiMenuItem
          key={item.title}
          emoji={item.context}
          index={index}
          isSelected={index === selectedIndex}
          onSelect={() => onSelect(item)}
          selector="[data-selector='tiptap-emoji-dropdown-menu']"
        />
      )
    })

    return rendered
  }, [items, selectedIndex, onSelect])

  if (!renderedItems.length) {
    return null
  }

  return (
    <Card
      style={{
        maxHeight: "var(--suggestion-menu-max-height)",
      }}
    >
      <CardBody className="min-h-0 w-full !overflow-hidden !p-0">
        <ScrollArea
          type="hover"
          className="w-full"
          viewClassName="max-h-[calc(var(--suggestion-menu-max-height)-2px)] rounded-none [&>div]:!block"
        >
          <div className="p-1.5">
            <ButtonGroup>{renderedItems}</ButtonGroup>
          </div>
        </ScrollArea>
      </CardBody>
    </Card>
  )
}
